/**
 * Firestore service for user's lists (wishlists, want lists, trade lists, etc.)
 */
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { auth, firestore } from './firebase';

import type { CardList } from '@/types/lists';
import type { CreateList, UpdateList } from '@/types/schemas';

// Firestore document type (raw from DB)
interface FirestoreList {
  name: string;
  color: string | null;
  icon: string | null;
  card_count: number;
  total_value: number;
  created_at: Timestamp;
  updated_at: Timestamp;
}

// Convert Firestore document to client type
function firestoreToList(id: string, data: FirestoreList): CardList {
  return {
    id,
    name: data.name,
    color: data.color ?? null,
    icon: data.icon ?? null,
    card_count: data.card_count ?? 0,
    total_value: data.total_value ?? 0,
    created_at: data.created_at?.toDate?.() ?? new Date(),
    updated_at: data.updated_at?.toDate?.() ?? new Date(),
  };
}

function getUserListsRef(userId: string) {
  return collection(firestore, 'users', userId, 'lists');
}

function getCurrentUserId(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
}

/**
 * Create a new list.
 */
export async function createList(data: CreateList): Promise<string> {
  const userId = getCurrentUserId();
  const listsRef = getUserListsRef(userId);

  const docData = {
    name: data.name,
    color: data.color ?? null,
    icon: data.icon ?? null,
    card_count: 0,
    total_value: 0,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  const docRef = await addDoc(listsRef, docData);
  return docRef.id;
}

/**
 * Update a list's properties.
 */
export async function updateList(listId: string, data: UpdateList): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'lists', listId);

  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp(),
  });
}

/**
 * Delete a list and all its cards.
 */
export async function deleteList(listId: string): Promise<void> {
  const userId = getCurrentUserId();
  const batch = writeBatch(firestore);

  // Delete all cards in this list
  const cardsRef = collection(firestore, 'users', userId, 'list_cards');
  const cardsQuery = query(cardsRef, where('list_id', '==', listId));
  const cardsSnapshot = await getDocs(cardsQuery);

  cardsSnapshot.docs.forEach((cardDoc) => {
    batch.delete(cardDoc.ref);
  });

  // Delete the list itself
  const listRef = doc(firestore, 'users', userId, 'lists', listId);
  batch.delete(listRef);

  await batch.commit();
}

/**
 * Get all lists for the current user.
 */
export async function getLists(): Promise<CardList[]> {
  const userId = getCurrentUserId();
  const listsRef = getUserListsRef(userId);
  const snapshot = await getDocs(listsRef);

  return snapshot.docs.map((docSnap) => firestoreToList(docSnap.id, docSnap.data() as FirestoreList));
}

/**
 * Subscribe to real-time updates of user's lists.
 * Returns an unsubscribe function.
 */
export function subscribeToLists(uid: string, callback: (lists: CardList[]) => void): () => void {
  const listsRef = collection(firestore, 'users', uid, 'lists');

  return onSnapshot(listsRef, (snapshot) => {
    const lists = snapshot.docs.map((docSnap) => firestoreToList(docSnap.id, docSnap.data() as FirestoreList));
    callback(lists);
  });
}
